import { useMemo, useRef, useState } from "react";
import { FolderOpen, Upload, Trash2, FileText, Search, X, Star, Tag, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Documents & Photos vault per trip: upload with category dropdown, colored
   category tag (right side) + date of entry, free-form colored tags, and
   internal search by text, tag, category and date range. */

// Deterministic color per free-form tag so the same tag is always the same color.
const TAG_PALETTE = [
  "bg-red-100 text-red-800 border-red-300", "bg-orange-100 text-orange-800 border-orange-300",
  "bg-amber-100 text-amber-800 border-amber-300", "bg-lime-100 text-lime-800 border-lime-300",
  "bg-emerald-100 text-emerald-800 border-emerald-300", "bg-cyan-100 text-cyan-800 border-cyan-300",
  "bg-blue-100 text-blue-800 border-blue-300", "bg-indigo-100 text-indigo-800 border-indigo-300",
  "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", "bg-pink-100 text-pink-800 border-pink-300",
];
const tagCls = (tag = "") => {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = (h * 31 + tag.toLowerCase().charCodeAt(i)) >>> 0;
  return TAG_PALETTE[h % TAG_PALETTE.length];
};

export const TagChip = ({ tag, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`file-tagchip-${tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    title={onClick ? `Search "${tag}"` : undefined}
    className={`inline-flex items-center gap-0.5 text-[11px] font-700 border px-1.5 py-0.5 ${tagCls(tag)} ${onClick ? "cursor-pointer hover:opacity-75" : "cursor-default"}`}>
    <Tag className="w-2.5 h-2.5" /> {tag}
  </button>
);

export const FILE_CATEGORIES = [
  { id: "boarding_pass", label: "Boarding Pass", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  { id: "ticket", label: "Ticket / Reservation", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  { id: "passport_id", label: "Passport / ID", cls: "bg-violet-100 text-violet-800 border-violet-300" },
  { id: "visa", label: "Visa", cls: "bg-purple-100 text-purple-800 border-purple-300" },
  { id: "hotel", label: "Hotel / Stay", cls: "bg-teal-100 text-teal-800 border-teal-300" },
  { id: "transport", label: "Transport", cls: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  { id: "insurance", label: "Insurance", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { id: "health", label: "Health / Vaccination", cls: "bg-green-100 text-green-800 border-green-300" },
  { id: "receipt", label: "Receipt", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { id: "itinerary", label: "Itinerary", cls: "bg-orange-100 text-orange-800 border-orange-300" },
  { id: "photo", label: "Photo", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  { id: "other", label: "Other", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
];
const catOf = (id) => FILE_CATEGORIES.find((c) => c.id === id) || FILE_CATEGORIES[FILE_CATEGORIES.length - 1];

export const CategoryTag = ({ category }) => {
  const c = catOf(category);
  return (
    <span className={`shrink-0 inline-block text-xs font-700 border px-2 py-0.5 ${c.cls}`} data-testid={`file-tag-${c.id}`}>
      {c.label}
    </span>
  );
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };

export default function TripFiles({ ownerType, ownerId, items, onChange, title = "Documents & Photos", coverId, onSetCover }) {
  const fileRef = useRef(null);
  const [cat, setCat] = useState("boarding_pass");
  const [label, setLabel] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [editing, setEditing] = useState(null);   // attachment id being tag-edited
  const [editTags, setEditTags] = useState("");

  const upload = async () => {
    if (!pending) { toast.error("Choose a file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pending);
      fd.append("owner_type", ownerType);
      fd.append("owner_id", ownerId);
      fd.append("category", cat);
      fd.append("label", label);
      fd.append("tags", tagsInput);
      await api.post("/trips/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPending(null); setLabel(""); setTagsInput("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Saved to your trip files");
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const remove = async (aid) => {
    try { await api.delete(`/trips/attachments/${aid}`); onChange?.(); }
    catch { toast.error("Could not delete"); }
  };

  const saveTags = async (aid) => {
    try {
      await api.put(`/trips/attachments/${aid}`, { tags: editTags });
      setEditing(null);
      onChange?.();
    } catch { toast.error("Could not save tags"); }
  };

  const token = localStorage.getItem("token");
  const filtered = useMemo(() => {
    let list = items || [];
    if (fCat) list = list.filter((a) => (a.category || "other") === fCat);
    if (q.trim()) {
      const t = q.trim().toLowerCase();
      list = list.filter((a) => [a.label, a.filename, catOf(a.category || "other").label, ...(a.tags || [])]
        .filter(Boolean).some((s) => s.toLowerCase().includes(t)));
    }
    if (from) list = list.filter((a) => a.uploaded_at && a.uploaded_at.slice(0, 10) >= from);
    if (to) list = list.filter((a) => a.uploaded_at && a.uploaded_at.slice(0, 10) <= to);
    return [...list].sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""));
  }, [items, q, fCat, from, to]);

  const filterActive = q || fCat || from || to;

  return (
    <section className="mb-10" data-testid="trip-files">
      <h2 className="font-heading font-900 text-emerald-700 text-2xl flex items-center gap-2 mb-4">
        <FolderOpen className="w-6 h-6" /> {title} <span className="text-sm font-500 text-muted-foreground">({(items || []).length})</span>
      </h2>

      {/* Upload row */}
      <div className="border border-zinc-300 bg-white p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={(e) => setPending(e.target.files?.[0] || null)} data-testid="file-input"
            className="text-sm file:mr-2 file:border file:border-zinc-300 file:bg-zinc-50 file:px-3 file:py-1.5 file:text-sm file:cursor-pointer" />
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="file-category-select"
            className="border border-zinc-300 px-2 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-primary">
            {FILE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (e.g. AA1234 boarding pass)"
            data-testid="file-label-input"
            className="flex-1 min-w-[160px] border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          <input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="Tags (comma separated: Paris, Day 2…)"
            data-testid="file-tags-input"
            className="flex-1 min-w-[160px] border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          <button onClick={upload} disabled={busy || !pending} data-testid="file-upload-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-700 text-white text-sm font-700 hover:bg-emerald-600 disabled:opacity-50">
            <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Add"}
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">JPG, PNG, WebP or PDF · max 8 MB · boarding passes, passports, visas, health cards, receipts, photos and more.</p>
      </div>

      {/* Search / filters */}
      {(items || []).length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="file-search-bar">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your files…" data-testid="file-search-input"
              className="w-full border border-zinc-300 pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <select value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="file-filter-category"
            className="border border-zinc-300 px-2 py-2 text-sm bg-white">
            <option value="">All categories</option>
            {FILE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <label className="text-xs text-muted-foreground">From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="file-filter-from" className="border border-zinc-300 px-2 py-1.5 text-sm ml-1" /></label>
          <label className="text-xs text-muted-foreground">To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="file-filter-to" className="border border-zinc-300 px-2 py-1.5 text-sm ml-1" /></label>
          {filterActive && (
            <button onClick={() => { setQ(""); setFCat(""); setFrom(""); setTo(""); }} data-testid="file-filter-clear"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-zinc-900"><X className="w-3.5 h-3.5" /> Clear</button>
          )}
        </div>
      )}

      {/* List */}
      {(items || []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No documents or photos yet — add your first one above.</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="file-no-results">No files match your search.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => {
            const isPdf = (a.content_type || "").includes("pdf");
            const href = `${process.env.REACT_APP_BACKEND_URL}${a.url}?auth=${token}`;
            return (
              <div key={a.id} className="border border-zinc-300 bg-white p-3 flex items-center gap-3" data-testid={`file-row-${a.id}`}>
                <a href={href} target="_blank" rel="noreferrer" className="shrink-0">
                  {isPdf
                    ? <span className="w-12 h-12 flex items-center justify-center bg-zinc-100 border border-zinc-200"><FileText className="w-6 h-6 text-rose-600" /></span>
                    : <img src={href} alt="" className="w-12 h-12 object-cover border border-zinc-200" loading="lazy" />}
                </a>
                <div className="flex-1 min-w-0">
                  <a href={href} target="_blank" rel="noreferrer" className="font-600 text-sm text-zinc-900 hover:text-primary truncate block">
                    {a.label || a.filename || "File"}
                  </a>
                  <div className="text-xs text-muted-foreground truncate">{a.filename}{a.uploaded_at ? ` · added ${fmtDate(a.uploaded_at)}` : ""}</div>
                  {editing === a.id ? (
                    <div className="flex items-center gap-1.5 mt-1.5" data-testid={`file-tag-editor-${a.id}`}>
                      <input value={editTags} onChange={(e) => setEditTags(e.target.value)} autoFocus
                        placeholder="Tags, comma separated" data-testid={`file-tag-edit-input-${a.id}`}
                        onKeyDown={(e) => e.key === "Enter" && saveTags(a.id)}
                        className="flex-1 min-w-0 border border-zinc-300 px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-primary" />
                      <button onClick={() => saveTags(a.id)} data-testid={`file-tag-save-${a.id}`}
                        className="p-1 text-emerald-700 hover:text-emerald-500" title="Save tags"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(null)} className="p-1 text-zinc-400 hover:text-zinc-600" title="Cancel"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (a.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`file-tags-${a.id}`}>
                      {a.tags.map((t) => <TagChip key={t} tag={t} onClick={() => setQ(t)} />)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="hidden sm:inline text-xs text-muted-foreground font-mono" data-testid={`file-date-${a.id}`}>{fmtDate(a.uploaded_at)}</span>
                  <CategoryTag category={a.category || "other"} />
                  <button onClick={() => { setEditing(a.id); setEditTags((a.tags || []).join(", ")); }}
                    title="Edit tags" data-testid={`file-tag-edit-${a.id}`}
                    className="p-1 text-zinc-300 hover:text-primary"><Pencil className="w-4 h-4" /></button>
                  {onSetCover && !isPdf && (
                    coverId === a.id
                      ? <button onClick={() => onSetCover(null)} title="Remove as trip cover" data-testid={`file-cover-unset-${a.id}`}
                          className="p-1 text-amber-500 hover:text-zinc-400"><Star className="w-4 h-4 fill-amber-400" /></button>
                      : <button onClick={() => onSetCover(a.id)} title="Set as trip cover photo" data-testid={`file-cover-set-${a.id}`}
                          className="p-1 text-zinc-300 hover:text-amber-500"><Star className="w-4 h-4" /></button>
                  )}
                  <button onClick={() => remove(a.id)} data-testid={`file-delete-${a.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
